import { useEffect, useState } from "react";
import { Users, Hash, ExternalLink, Shield, MessageSquare, Zap, Globe2, AlertCircle, Crown } from "lucide-react";

const GUILD_ID = "1489141395456262346";
const FALLBACK_INVITE = "https://discord.gg/territoria";
const WIDGET_URL = `https://discord.com/api/guilds/${GUILD_ID}/widget.json`;

const STATUS = {
  online: { label: "online", dot: "#43b581" },
  idle: { label: "idle", dot: "#faa61a" },
  dnd: { label: "dnd", dot: "#f04747" },
  offline: { label: "offline", dot: "#747f8d" },
};

const PERKS = [
  { icon: Zap, title: "Live Game Updates", body: "Patch notes, balance changes, and downtime alerts hit Discord first — before anywhere else." },
  { icon: Shield, title: "Diplomacy Channels", body: "Coordinate alliances, broker treaties, and stage operations across the World Stage." },
  { icon: Crown, title: "Nation Recruitment", body: "Established powers post citizen openings. Newcomers find a flag to fly." },
  { icon: MessageSquare, title: "Strategy & Help", body: "Veterans drop guides for the Native Calculator, mining loops, and combat math." },
  { icon: Globe2, title: "Lore & Roleplay", body: "Carve your nation's backstory into the canon of the Territoria universe." },
  { icon: Users, title: "Devs in the Room", body: "Talk directly to the team. Suggestions get heard. Bugs get tracked." },
];

export default function Discord() {
  const [widget, setWidget] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error

  useEffect(() => {
    fetch(WIDGET_URL)
      .then(r => r.ok ? r.json() : Promise.reject(new Error("widget_disabled")))
      .then(d => { setWidget(d); setState("ok"); })
      .catch(() => setState("error"));
  }, []);

  const invite = widget?.instant_invite || FALLBACK_INVITE;
  const onlineCount = widget?.presence_count ?? null;
  const members = widget?.members || [];
  // Sort: online → idle → dnd
  const sorted = [...members].sort((a, b) => {
    const order = { online: 0, idle: 1, dnd: 2, offline: 3 };
    return (order[a.status] ?? 4) - (order[b.status] ?? 4);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-16" data-testid="discord-page">
      {/* HERO */}
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-14 items-center mb-12 md:mb-16">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-[#7289DA] uppercase">// Citizen Comms</span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-[#43b581] uppercase">
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-[#43b581] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#43b581]" />
              </span>
              live
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--t-text)] uppercase leading-[1.02] mb-5">
            Join the <span className="text-[#7289DA]">Community</span>
          </h1>
          <p className="text-base md:text-lg text-[var(--t-text-dim)] max-w-xl leading-relaxed mb-7">
            Territoria&apos;s nerve center lives on Discord. Patch notes drop here first.
            Alliances form here. Wars are declared here. This is where the meta is written.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={invite}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2.5 px-6 py-3 bg-[#5865F2] text-white font-display text-sm tracking-[0.18em] uppercase border border-[#5865F2] hover:bg-[#4752c4] hover:border-[#4752c4] transition shadow-[0_0_28px_rgba(88,101,242,0.45)]"
              data-testid="discord-join-btn"
            >
              <DiscordIcon className="w-5 h-5" />
              Join the Server
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </a>
            {onlineCount !== null && (
              <div className="flex items-center gap-2 font-mono text-xs tracking-[0.22em] text-[var(--t-text-dim)] uppercase" data-testid="discord-online-count">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inset-0 rounded-full bg-[#43b581] opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#43b581]" />
                </span>
                <span className="text-[#43b581] font-semibold">{onlineCount}</span> citizens online now
              </div>
            )}
          </div>
        </div>

        {/* LIVE WIDGET CARD */}
        <div className="panel panel-glow p-5 md:p-6 relative overflow-hidden" data-testid="discord-widget-card">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#5865F2]/20 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#5865F2] flex items-center justify-center" style={{ clipPath: "polygon(15% 0,100% 0,100% 85%,85% 100%,0 100%,0 15%)" }}>
                  <DiscordIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-display text-base text-[var(--t-cyan)] uppercase tracking-[0.14em]" data-testid="discord-server-name">{widget?.name || "Territoria"}</div>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">Official Server</div>
                </div>
              </div>
              <Hash className="w-4 h-4 text-[#7289DA]" />
            </div>

            {state === "loading" && (
              <div className="py-10 text-center font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow" data-testid="discord-loading">// linking to citadel...</div>
            )}

            {state === "error" && (
              <div className="py-6 px-4 border border-[var(--t-border)] bg-[rgba(240,71,71,0.06)] flex gap-3 items-start" data-testid="discord-error">
                <AlertCircle className="w-4 h-4 text-[var(--t-red)] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[var(--t-text-dim)] leading-relaxed">
                  Live presence is temporarily offline. The doors are still open — click <strong className="text-[var(--t-cyan)]">Join the Server</strong> above.
                </div>
              </div>
            )}

            {state === "ok" && (
              <>
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-[var(--t-border)]">
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-[var(--t-text-muted)] uppercase">
                    <Users className="w-3.5 h-3.5" /> {onlineCount} online
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.22em] text-[#43b581] uppercase">{members.length} visible</div>
                </div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1 custom-scroll" data-testid="discord-members-list">
                  {sorted.map(m => {
                    const s = STATUS[m.status] || STATUS.offline;
                    return (
                      <div key={m.id} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-[rgba(88,101,242,0.08)]" data-testid={`discord-member-${m.id}`}>
                        <div className="relative flex-shrink-0">
                          <img src={m.avatar_url} alt="" className="w-7 h-7 rounded-full bg-[#2c2f33]" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[var(--t-bg)]" style={{ background: s.dot }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-[var(--t-text)] truncate">{m.username}</div>
                          {m.game?.name && (
                            <div className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.1em] truncate">▶ {m.game.name}</div>
                          )}
                        </div>
                        <span className="font-mono text-[9px] tracking-[0.2em] uppercase flex-shrink-0" style={{ color: s.dot }}>{s.label}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* PERKS GRID */}
      <div className="mb-10">
        <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Why Citizens Stay</span>
        <h2 className="font-display text-2xl md:text-3xl text-[var(--t-cyan)] uppercase mt-2 mb-7">Six Reasons to Pin the Tab</h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {PERKS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="panel p-5 hover:border-[#5865F2] transition" data-testid={`discord-perk-${i}`}>
                <div className="w-9 h-9 mb-3 bg-[rgba(88,101,242,0.12)] border border-[#5865F2]/40 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#7289DA]" />
                </div>
                <h3 className="font-display text-sm md:text-base text-[var(--t-text)] uppercase tracking-[0.12em] mb-2">{p.title}</h3>
                <p className="text-sm text-[var(--t-text-dim)] leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* BOTTOM CTA */}
      <div className="panel panel-glow p-7 md:p-9 text-center relative overflow-hidden" data-testid="discord-bottom-cta">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(88,101,242,0.12)_0%,_transparent_60%)] pointer-events-none" />
        <div className="relative">
          <DiscordIcon className="w-10 h-10 mx-auto mb-4 text-[#7289DA]" />
          <h2 className="font-display text-2xl md:text-3xl text-[var(--t-text)] uppercase mb-3">The Empire Talks. <span className="text-[#7289DA]">You Listen.</span></h2>
          <p className="text-sm md:text-base text-[var(--t-text-dim)] max-w-xl mx-auto mb-6">
            Free. No spam. Leave anytime. The only thing you&apos;ll regret is not joining sooner.
          </p>
          <a
            href={invite}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#5865F2] text-white font-display text-sm tracking-[0.2em] uppercase border border-[#5865F2] hover:bg-[#4752c4] hover:border-[#4752c4] transition shadow-[0_0_32px_rgba(88,101,242,0.5)]"
            data-testid="discord-cta-btn"
          >
            <DiscordIcon className="w-5 h-5" />
            Join Now
            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
          </a>
        </div>
      </div>
    </div>
  );
}

function DiscordIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}
